/**
 * Permissions mirror App\Security\Permission on the backend.
 *
 * The client uses them only to decide what to show. Every one of them is
 * re-checked server-side by a voter: hiding a button is a courtesy, not a
 * security control.
 */
export const Permission = {
  personView: 'person.view',
  personManage: 'person.manage',
  membershipView: 'membership.view',
  membershipManage: 'membership.manage',
  animalView: 'animal.view',
  animalViewOwn: 'animal.view_own',
  animalManage: 'animal.manage',
  animalManageOwn: 'animal.manage_own',
  animalDeregister: 'animal.deregister',
  parentageView: 'parentage.view',
  parentageManage: 'parentage.manage',
  declarationView: 'declaration.view',
  declarationSubmit: 'declaration.submit',
  declarationReview: 'declaration.review',
  declarationDecide: 'declaration.decide',
  declarationRegister: 'declaration.register',
  certificateIssue: 'certificate.issue',
  certificateDownload: 'certificate.download',
  healthView: 'health.view',
  healthManage: 'health.manage',
  healthUpload: 'health.upload',
  listingView: 'listing.view',
  listingManage: 'listing.manage',
  orderView: 'order.view',
  orderManage: 'order.manage',
  tenantSettings: 'tenant.settings',
  tenantUsers: 'tenant.users',
  tenantSpecies: 'tenant.species',
  lookupManage: 'lookup.manage',
  billingView: 'billing.view',
  billingManage: 'billing.manage',
  auditView: 'audit.view',
} as const

export type PermissionName = (typeof Permission)[keyof typeof Permission]

export type TenantRole =
  | 'ROLE_TENANT_OWNER'
  | 'ROLE_TENANT_ADMIN'
  | 'ROLE_TENANT_STAFF'
  | 'ROLE_TENANT_REGISTRAR'
  | 'ROLE_BREEDER'
  | 'ROLE_MEMBER'
  | 'ROLE_VIEWER'
  | 'ROLE_BUYER'

export function can(permissions: readonly string[], permission: PermissionName): boolean {
  return permissions.includes(permission)
}

export function canAny(permissions: readonly string[], ...wanted: PermissionName[]): boolean {
  return wanted.some((permission) => permissions.includes(permission))
}
